// Copyright (c) 2024 by Audere

import { Box, Chip } from "@mui/material";
import React from "react";
import { getStatusGroups } from "../features/data/annotatorStatusUtils";
import {
  LabelerData,
  LabelingStatus,
  QCData,
} from "../features/api/labelApiSlice";
import { createStyleSheet } from "../utils/styles";
import { statusChipStyles } from "./SmallChip";
import { formatStatusText } from "../features/data/conversationUtils";
import AnnotatorBadge from "./AnnotatorBadge";
import { useCurrentUser } from "../hooks/useCurrentUser";

const AnnotatorBadgeGroup: React.FC<{
  annotators: (LabelerData | QCData)[];
}> = ({ annotators }) => {
  const user = useCurrentUser();
  const statusGroups = getStatusGroups(annotators, user.email);
  const qcStatuses = new Set([
    LabelingStatus.qc_in_progress,
    LabelingStatus.qc_complete,
  ]);
  const labelStatuses = new Set([
    LabelingStatus.label_in_progress,
    LabelingStatus.label_complete,
  ]);
  const allStatuses = new Set([
    ...Array.from(qcStatuses),
    ...Array.from(labelStatuses),
  ]);
  const annotatorStatusGroups = statusGroups.filter((group) => {
    const status = group.status;
    if (user.isMonitor) {
      return allStatuses.has(status);
    }
    if (user.isQC) {
      return qcStatuses.has(status);
    }
    return labelStatuses.has(status);
  });

  return (
    <Box sx={styles.contentContainer}>
      {annotatorStatusGroups.map(({ status, annotators: labelers }, index) => (
        <Box key={index}>
          <Chip
            label={`${formatStatusText(status)} (${labelers.length})`}
            sx={statusChipStyles[status as keyof typeof statusChipStyles]}
            size="small"
          />
          <AnnotatorBadge annotators={labelers} group={status} />
        </Box>
      ))}
    </Box>
  );
};

const styles = createStyleSheet({
  contentContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    textAlign: "center",
    justifyContent: "center",
  },
});

export default AnnotatorBadgeGroup;
